/**
 * News/NewsCategoriesAdmin.jsx
 * ════════════════════════════════════════════════════════════════
 * Керування довідником категорій новин. Тільки admin.
 * Модальне вікно поверх NewsAdmin.
 *
 * Props:
 *   onClose {function}
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faPlus, faBoxArchive, faRotateLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { newsCategoriesAPI } from './newsAPI';

var COLOR_PRESETS = ['#0069ff', '#059669', '#d97706', '#dc3545', '#7c3aed', '#0891b2', '#be185d', '#6b7280'];

var NewsCategoriesAdmin = function({ onClose }) {
    var [categories, setCategories] = useState([]);
    var [loading, setLoading]       = useState(true);
    var [newName, setNewName]       = useState('');
    var [newColor, setNewColor]     = useState(COLOR_PRESETS[0]);
    var [creating, setCreating]     = useState(false);
    var [editingId, setEditingId]   = useState(null);
    var [editName, setEditName]     = useState('');

    var load = function() {
        setLoading(true);
        newsCategoriesAPI.list()
            .then(setCategories)
            .catch(function() { toast.error('Помилка завантаження категорій'); })
            .finally(function() { setLoading(false); });
    };

    useEffect(load, []);

    var handleCreate = async function() {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await newsCategoriesAPI.create({ name: newName.trim(), color: newColor, order: categories.length + 1 });
            setNewName('');
            setNewColor(COLOR_PRESETS[0]);
            load();
            toast.success('Категорію створено');
        } catch (e) {
            toast.error(e.message || 'Помилка створення категорії');
        } finally {
            setCreating(false);
        }
    };

    var handleToggleStatus = async function(cat) {
        try {
            await newsCategoriesAPI.changeStatus(cat._id, cat.status === 'active' ? 'archived' : 'active');
            load();
        } catch (e) {
            toast.error('Помилка зміни статусу');
        }
    };

    var startEdit = function(cat) {
        setEditingId(cat._id);
        setEditName(cat.name);
    };

    var saveEdit = async function(cat) {
        if (!editName.trim()) return;
        try {
            await newsCategoriesAPI.update(cat._id, { name: editName.trim() });
            setEditingId(null);
            load();
        } catch (e) {
            toast.error(e.message || 'Помилка збереження');
        }
    };

    var active   = categories.filter(function(c) { return c.status === 'active'; });
    var archived = categories.filter(function(c) { return c.status === 'archived'; });

    return (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered" onClick={function(e) { e.stopPropagation(); }}>
                <div className="modal-content" style={{ borderRadius: 14 }}>

                    <div className="modal-header" style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <h5 className="modal-title" style={{ fontWeight: 700 }}>Категорії новин</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>

                    <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>

                        {/* Форма додавання */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            <div style={{ display: 'flex', gap: 4 }}>
                                {COLOR_PRESETS.map(function(c) {
                                    return (
                                        <div
                                            key={c}
                                            onClick={function() { setNewColor(c); }}
                                            style={{
                                                width: 24, height: 24, borderRadius: '50%', background: c,
                                                cursor: 'pointer', flexShrink: 0,
                                                border: newColor === c ? '2px solid #111827' : '2px solid transparent',
                                                boxShadow: newColor === c ? '0 0 0 2px #fff inset' : 'none',
                                            }}
                                        />
                                    );
                                })}
                            </div>
                            <input
                                type="text"
                                value={newName}
                                onChange={function(e) { setNewName(e.target.value); }}
                                onKeyDown={function(e) { if (e.key === 'Enter') handleCreate(); }}
                                placeholder="Назва нової категорії"
                                maxLength={60}
                                style={{ flex: 1, height: 34, border: '1px solid #ced4da', borderRadius: 6, padding: '0 10px', fontSize: 13 }}
                            />
                            <button
                                type="button" onClick={handleCreate} disabled={creating || !newName.trim()}
                                style={{
                                    width: 34, height: 34, borderRadius: 6, border: 'none',
                                    background: '#0069ff', color: '#fff', flexShrink: 0,
                                    cursor: 'pointer', opacity: !newName.trim() ? 0.5 : 1,
                                }}
                            >
                                {creating ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <FontAwesomeIcon icon={faPlus} />}
                            </button>
                        </div>

                        {loading ? (
                            <div className="text-center py-4"><Spinner animation="border" size="sm" variant="primary" /></div>
                        ) : (
                            <>
                                {/* Активні */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: archived.length ? 20 : 0 }}>
                                    {active.map(function(cat) {
                                        return (
                                            <div key={cat._id} style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8,
                                            }}>
                                                <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                                                {editingId === cat._id ? (
                                                    <input
                                                        type="text" value={editName} autoFocus
                                                        onChange={function(e) { setEditName(e.target.value); }}
                                                        onKeyDown={function(e) { if (e.key === 'Enter') saveEdit(cat); if (e.key === 'Escape') setEditingId(null); }}
                                                        onBlur={function() { saveEdit(cat); }}
                                                        style={{ flex: 1, border: '1px solid #86b7fe', borderRadius: 4, padding: '2px 6px', fontSize: 13 }}
                                                    />
                                                ) : (
                                                    <span
                                                        onClick={function() { startEdit(cat); }}
                                                        style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#111827', cursor: 'text' }}
                                                    >
                                                        {cat.name}
                                                    </span>
                                                )}
                                                <button
                                                    type="button" onClick={function() { handleToggleStatus(cat); }}
                                                    title="Архівувати"
                                                    style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', color: '#9ca3af', cursor: 'pointer' }}
                                                >
                                                    <FontAwesomeIcon icon={faBoxArchive} style={{ fontSize: 12 }} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                    {active.length === 0 && (
                                        <div style={{ fontSize: 12, color: '#adb5bd', textAlign: 'center', padding: 10 }}>Немає активних категорій</div>
                                    )}
                                </div>

                                {/* Архівовані */}
                                {archived.length > 0 && (
                                    <div>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                            Архів
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {archived.map(function(cat) {
                                                return (
                                                    <div key={cat._id} style={{
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                        padding: '8px 10px', border: '1px solid #f0f0f0', borderRadius: 8,
                                                        opacity: 0.6,
                                                    }}>
                                                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: cat.color, flexShrink: 0 }} />
                                                        <span style={{ flex: 1, fontSize: 13, color: '#6b7280' }}>{cat.name}</span>
                                                        <button
                                                            type="button" onClick={function() { handleToggleStatus(cat); }}
                                                            title="Відновити"
                                                            style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', color: '#0069ff', cursor: 'pointer' }}
                                                        >
                                                            <FontAwesomeIcon icon={faRotateLeft} style={{ fontSize: 12 }} />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer" style={{ borderTop: '1px solid #f0f0f0' }}>
                        <button type="button" className="btn btn-primary" onClick={onClose}>Готово</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewsCategoriesAdmin;